import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import toColumnLabel from 'notebook/structured-tables/columns/toColumnLabel';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {isCellLoading} from 'notebook/tables/excess/Util';
import * as DownloadAsCSVDAO from 'notebook/tables/export/DownloadAsCSVDAO';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {toFilename} from 'util/FileUtil';
import {initiateTextDownload} from 'util/TextToBlobUtil';

import {
  STRUCTURED_TABLE_KINDS_TO_SHOW_CSV_METADATA,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from '../StructuredTableConstants';
import buildRowUiData from '../util/buildRowUiData';
import makeSimpleActionFunction from './makeSimpleActionFunction';

/**
 * An ActionFunction that prepares a CSV export to download of a
 * StructuredTable from within the browser.
 */
export const downloadCSVActionFunction = makeSimpleActionFunction({
  label: 'Download as CSV',
  icon: 'download',
  onInvoke: async ({
    structuredTable,
    structuredTableAdapter,
    computedTable,
    liveRowMetadataByRow,
    actionFunctionContext,
    entryOrTemplateId,
  }) => {
    const tableName = structuredTable.name.length > 0 ? structuredTable.name : 'table';
    initiateTextDownload(
      await makeTableCSV({
        table: structuredTableAdapter.toTable(structuredTable).table,
        computedTable,
        headerRow: structuredTable.columns.map(
          (structuredTableColumn) =>
            toColumnLabel({structuredTableColumn, schemas: structuredTable.schemas}) || 'unknown'
        ),
        rowUiData: STRUCTURED_TABLE_KINDS_TO_SHOW_CSV_METADATA.includes(structuredTable.kind)
          ? buildRowUiData(structuredTable, liveRowMetadataByRow)
          : undefined,
        includeCellValueModifiers: false,
      }),
      toFilename(tableName, 'csv')
    );
    auditDownloadTableAsCSV(
      structuredTable,
      structuredTableAdapter,
      entryOrTemplateId,
      actionFunctionContext
    );
  },
  getDisabled: ({structuredTable, computedTable}) =>
    structuredTable.rows.length === 0 || isCellInTableLoading(computedTable),
});

function isCellInTableLoading(computedTable: Array<Array<EvaluatedCell>>): boolean {
  return computedTable.some((row) => row.some((cell) => isCellLoading(cell)));
}

function auditDownloadTableAsCSV(
  structuredTable: UnknownStructuredTable,
  structuredTableAdapter: UnknownStructuredTableAdapter,
  documentLikeId: string | undefined | null,
  actionFunctionContext: ActionFunctionContext
): void {
  const parentAuditIds: Array<string> = [];
  // Add the potential parent audit ids
  if (documentLikeId) {
    parentAuditIds.push(documentLikeId);
  }
  if (actionFunctionContext.procedureMethodDefinitionVersionId) {
    parentAuditIds.push(actionFunctionContext.procedureMethodDefinitionVersionId);
  }
  if (actionFunctionContext.procedureId) {
    parentAuditIds.push(actionFunctionContext.procedureId);
  }
  // If there are no auditable parents, don't record an event as it won't be even show up anywhere.
  if (!parentAuditIds.length) {
    return;
  }
  DownloadAsCSVDAO.auditDownloadTableAsCSV({
    tableName: structuredTable.name || structuredTableAdapter.getDisplayName(structuredTable),
    structuredTableProvenanceUuid: structuredTable.provenanceUuid,
    documentLikeId: documentLikeId ?? undefined,
    parentAuditIds,
  });
}
